import banIcon from "@tabler/icons/outline/ban.svg";
import eyeOffIcon from "@tabler/icons/outline/eye-off.svg";
import trashIcon from "@tabler/icons/outline/trash.svg";
import React, { Suspense, type ComponentProps, useMemo } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import { useLocation, useMatch, useNavigate } from "react-router-dom";

import { useGroupMembershipRequests } from "@/api/group-membership-requests";
import { Column, Icon, Layout, Stack, Tabs, Text } from "@/components/ui";
import { useAuth } from "@/contexts/auth/useAuth";
import { useGroupContext } from "@/contexts/group-context";
import { useModalContext } from "@/contexts/modal-context";
import GroupHeader from "@/features/group/components/group-header";
import LinkFooter from "@/features/ui/components/link-footer";
import {
  CtaBanner,
  GroupMediaPanel,
  SignUpPanel,
  SuggestedGroupsPanel,
  TrendingEventsPanel,
} from "@/features/ui/util/async-components";
import { GroupRoles } from "@/schemas/group-member";

import type { Group } from "@/schemas";

const messages = defineMessages({
  all: { id: "group.tabs.all", defaultMessage: "All" },
  about: { id: "group.tabs.about", defaultMessage: "About" },
  media: { id: "group.tabs.media", defaultMessage: "Media" },
  tags: { id: "group.tabs.tags", defaultMessage: "Topics" },
  discussion: { id: "group.tabs.discussion", defaultMessage: "Discussion" },
  conversations: {
    id: "group.tabs.conversations",
    defaultMessage: "Conversations",
  },
  events: { id: "group.tabs.events", defaultMessage: "Events" },
});

interface GroupPageProps {
  children: React.ReactNode;
}

const DeletedBlankslate = () => (
  <Stack space={4} className="py-10" alignItems="center">
    <div className="rounded-full bg-danger-200 p-3 dark:bg-danger-400/20">
      <Icon
        src={trashIcon}
        className="size-6 text-danger-600 dark:text-danger-400"
      />
    </div>

    <Text theme="muted">
      <FormattedMessage
        id="group.deleted.message"
        defaultMessage="This group has been deleted."
      />
    </Text>
  </Stack>
);

const PrivacyBlankslate = () => (
  <Stack space={4} className="py-10" alignItems="center">
    <div className="rounded-full bg-gray-200 p-3 dark:bg-gray-800">
      <Icon
        src={eyeOffIcon}
        className="size-6 text-gray-600 dark:text-gray-600"
      />
    </div>

    <Text theme="muted">
      <FormattedMessage
        id="group.private.message"
        defaultMessage="Content is only visible to group members"
      />
    </Text>
  </Stack>
);

const BlockedBlankslate = ({ group }: { group: Group }) => (
  <Stack space={4} className="py-10" alignItems="center">
    <div className="rounded-full bg-danger-200 p-3 dark:bg-danger-400/20">
      <Icon
        src={banIcon}
        className="size-6 text-danger-600 dark:text-danger-400"
      />
    </div>

    <Text theme="muted">
      <FormattedMessage
        id="group.banned.message"
        defaultMessage="You are banned from {group}"
        values={{
          group: (
            <Text
              theme="inherit"
              tag="span"
              dangerouslySetInnerHTML={{ __html: group.display_name_html }}
            />
          ),
        }}
      />
    </Text>
  </Stack>
);

/** Page to display a group. */
const GroupPage = ({ children }: GroupPageProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const { group, groupId } = useGroupContext();
  const { currentAccount } = useAuth();
  const { openModal } = useModalContext();

  const isOnAboutPage = !!useMatch("/group/:groupSlug/about");

  const { totalCount } = useGroupMembershipRequests(groupId);

  const isPredictionGroup = !!group.event_category_name;
  const isMember = !!group.relationship?.member;
  const isBlocked = group.relationship?.blocked_by;
  const isPrivate = group.group_visibility === "members_only";
  const isDeleted = !!group.deleted_at;
  const isOwner = group.relationship?.role === GroupRoles.OWNER;
  const isAdmin = group.relationship?.role === GroupRoles.ADMIN;

  const tabItems = useMemo(() => {
    const items: ComponentProps<typeof Tabs>["items"] = [];
    const doTabAction = (url: string) =>
      currentAccount ? navigate(url) : openModal("UNAUTHORIZED");

    if (isPredictionGroup) {
      items.push({
        label: intl.formatMessage(messages.conversations),
        action: () => navigate(`/group/${group.slug}`),
        name: `/group/${group.slug}`,
      });

      items.push({
        label: intl.formatMessage(messages.events),
        action: () => navigate(`/group/${group.slug}/events`),
        name: `/group/${group.slug}/events`,
      });

      return items;
    }
    items.push({
      label: intl.formatMessage(messages.all),
      action: () => navigate(`/group/${group.slug}`),
      name: `/group/${group.slug}`,
    });
    items.push({
      label: intl.formatMessage(messages.tags),
      action: () => doTabAction(`/group/${group.slug}/tags`),
      name: `/group/${group.slug}/tags`,
    });
    if (isMember) {
      items.push({
        label: intl.formatMessage(messages.media),
        action: () => doTabAction(`/group/${group.slug}/media`),
        name: `/group/${group.slug}/media`,
      });
    }
    const shouldShowIndicator =
      (isAdmin || isOwner) && totalCount > 0 && !isOnAboutPage;
    items.push({
      label: intl.formatMessage(messages.about),
      action: () => doTabAction(`/group/${group.slug}/about`),
      name: `/group/${group.slug}/about`,
      showIndicator: shouldShowIndicator,
    });
    return items;
  }, [
    totalCount,
    group.slug,
    isOnAboutPage,
    isAdmin,
    isOwner,
    isMember,
    currentAccount,
    isPredictionGroup,
  ]);

  const renderChildren = () => {
    if (isDeleted) {
      return <DeletedBlankslate />;
    } else if (!isMember && isPrivate) {
      return <PrivacyBlankslate />;
    } else if (isBlocked) {
      return <BlockedBlankslate group={group} />;
    } else {
      return children;
    }
  };

  return (
    <>
      <Layout.Main>
        <Column label={group.display_name} withHeader>
          <div className="-m-4">
            <GroupHeader group={group} isPredictionGroup={isPredictionGroup} />

            <Tabs
              key={`group-tabs-${groupId}`}
              items={tabItems}
              activeItem={location.pathname}
            />

            {renderChildren()}
          </div>
        </Column>

        {!currentAccount && (
          <Suspense fallback={null}>
            <CtaBanner key="cta-banner" />
          </Suspense>
        )}
      </Layout.Main>

      <Layout.Aside>
        {!currentAccount && (
          <Suspense fallback={null}>
            <SignUpPanel key="sign-up-panel" />
          </Suspense>
        )}

        {currentAccount && !isPredictionGroup && (
          <Suspense fallback={null}>
            <GroupMediaPanel group={group} />
          </Suspense>
        )}

        {currentAccount && !isPredictionGroup && (
          <Suspense fallback={null}>
            <SuggestedGroupsPanel />
          </Suspense>
        )}

        {isPredictionGroup && (
          <Suspense fallback={null}>
            <TrendingEventsPanel />
          </Suspense>
        )}

        <LinkFooter key="link-footer" />
      </Layout.Aside>
    </>
  );
};

export default GroupPage;
